import React, { useRef } from "react";
import { Helmet } from "react-helmet-async";
import Footer from "../../components/Footer";
import { useArticlesContext } from "../../context/ArticlesContext";
import MobileMapOverlay from "../../components/MobileMapOverlay";
import SocialMedia from "../../components/SocialMedia";
import Marker from "../../components/Marker";
import Top from "../../components-top/Top";
import Left from "../../components-top/Left";
import Right from "../../components-top/Right";
import Bottom from "../../components-top/Bottom";

export default function CanadianRockiesVisit() {
  // 1) Provide a unique reference ID (the slug in your ArticlesContext)
  const ref_id = "best-time-to-visit-canadian-rockies";

  // 2) Access articles from your context (optional usage)
  const { articles } = useArticlesContext();
  const ref_data = articles.find((art) => art.slug === ref_id);

  // =========================================================
  // ========== SECTION TITLES IDS (Labels Map) ==============
  // =========================================================
  const labelsMap = {
    intro: "Introduction",
    "section-1": "Winter (December – March)",
    "section-2": "Spring (April – May)",
    "section-3": "Summer (June – August)",
    "section-4": "Fall (September – November)",
    "locations": "Top Locations",
    "local-regulations": "Local Regulations",
    faq: "FAQ",
    conclusion: "Conclusion",
    references: "References",
  };

  // ===================================
  // 4) References Data
  // ===================================
  const referencesData = [
    {
      url: "https://parks.canada.ca/pn-np/ab/banff",
      label: "Parks Canada – Banff National Park",
    },
    {
      url: "https://511.alberta.ca",
      label: "511 Alberta - Road Conditions",
    },
    {
      url: "https://www.jasper.travel/",
      label: "Official Jasper Tourism",
    },
  ];

  // ===================================
  // 5) Locations for the Leaflet map
  // ===================================
  // We'll match each location with a distinct ID below in the text
  const locations = [
    {
      id: "banff-national-park",
      lat: 51.17778,
      lng: -115.57361,
      popupText: "Banff National Park (51.17778, -115.57361)",
    },
    {
      id: "lake-louise",
      lat: 51.40699,
      lng: -116.22350,
      popupText: "Lake Louise (51.40699, -116.22350)",
    },
    {
      id: "moraine-lake",
      lat: 51.32250,
      lng: -116.18556,
      popupText: "Moraine Lake (51.32250, -116.18556)",
    },
    {
      id: "icefields-parkway",
      lat: 51.42601,
      lng: -116.18553,
      popupText: "Icefields Parkway",
    },
    {
      id: "athabasca-glacier",
      lat: 52.19861,
      lng: -117.24361,
      popupText: "Athabasca Glacier (52.19861, -117.24361)",
    },
    {
      id: "jasper-town",
      lat: 52.87306,
      lng: -118.08222,
      popupText: "Jasper Town (52.87306, -118.08222)",
    },
    {
      id: "athabasca-falls",
      lat: 52.65750,
      lng: -117.88333,
      popupText: "Athabasca Falls (52.65750, -117.88333)",
    },
    {
      id: "takakkaw-falls",
      lat: 51.49806,
      lng: -116.47250,
      popupText: "Takakkaw Falls (51.49806, -116.47250)",
    },
    {
      id: "emerald-lake",
      lat: 51.44400,
      lng: -116.53100,
      popupText: "Emerald Lake (51.44400, -116.53100)",
    },
    {
      id: "radium-hot-springs",
      lat: 50.61972,
      lng: -116.07306,
      popupText: "Radium Hot Springs (50.61972, -116.07306)",
    },
  ];

  // Keep references to textual sections for scroll linking
  const spotRefs = useRef({});
  const setSpotRef = (id, el) => {
    spotRefs.current[id] = el;
  };

  // Desktop map reference
  const mapRef = useRef(null);

  // MOBILE MAP
  const mobileMapRef = useRef(null);

  return (
    <>
      {/* ============ SEO & METADATA ============ */}
      <Helmet>
        <title>Best Time to Visit the Canadian Rockies: Seasonal Guide & Top Locations</title>
        <meta
          name="description"
          content="Discover the ideal time to visit the Canadian Rockies with a comprehensive seasonal breakdown, top locations, local regulations, FAQ, and more."
        />
        <meta
          name="keywords"
          content="Canadian Rockies, Banff, Jasper, travel tips, best time to visit"
        />
        <meta name="robots" content="index, follow" />
        <link
          rel="canonical"
          href="https://www.bcouver.com/articles/best-time-to-visit-canadian-rockies.html"
        />

        {/* Open Graph / Facebook */}
        <meta
          property="og:title"
          content="Best Time to Visit the Canadian Rockies: Seasonal Guide & Top Locations"
        />
        <meta
          property="og:description"
          content="Your essential guide to exploring the Canadian Rockies year-round. Includes local regulations, top spots, FAQ, and more."
        />
        <meta
          property="og:image"
          content="https://bcouver.com/assets/images/best-time-to-visit-canadian-rockies.jpg"
        />
        <meta
          property="og:url"
          content="https://www.bcouver.com/articles/best-time-to-visit-canadian-rockies.html"
        />
        <meta property="og:type" content="article" />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Best Time to Visit the Canadian Rockies: Seasonal Guide & Top Locations"
        />
        <meta
          name="twitter:description"
          content="Year-round tips for traveling through the Canadian Rockies. Stay informed on regulations, locations, and more."
        />
        <meta
          name="twitter:image"
          content="https://bcouver.com/assets/images/best-time-to-visit-canadian-rockies.jpg"
        />
      </Helmet>

      {/* ============ TOP NAVBAR + HERO ============ */}
      <Top ref_id={ref_id} />

      <main>
        <div className="container article-main">
          {/* LEFT COLUMN (TableOfContents + Map) */}
          <Left
            labelsMap={labelsMap}
            locations={locations}
            mapRef={mapRef}
            spotRefs={spotRefs}
          />

          {/* ============ ARTICLE CONTENT ============ */}
          <article className="article-content">
            {/* Featured Image */}
            <img
              src={ref_data?.image || "https://bcouver.com/assets/images/article-fallback.jpg"}
              alt="Sunrise over the Canadian Rockies"
              className="featured-image"
            />

            {/* Social Share */}
            <SocialMedia />

            {/* SECTION: Introduction */}
            <h2 id="intro">Introduction</h2>
            <p>
              The Canadian Rockies are a dream destination for travelers around
              the world. Home to some of the most dramatic landscapes in North
              America—towering peaks, emerald lakes, wildlife-filled forests—this
              region is an outdoor enthusiast’s paradise year-round. Yet, the best
              time to visit truly depends on what experiences you crave. Do you
              want to hike blooming alpine meadows, glide across a frozen lake,
              or catch the fall foliage? In this guide, we break down each season,
              highlight the most famous spots (<strong>with precise coordinates</strong>),
              discuss local regulations, and answer your most pressing FAQs to help
              you plan an unforgettable trip.
            </p>

            {/* SECTION 1: Winter */}
            <h2 id="section-1">Winter (December – March)</h2>
            <p>
              Winter in the Canadian Rockies is a serene wonderland blanketed by
              snow. Temperatures often dip well below freezing, especially at
              higher elevations, but you’re rewarded with fewer crowds, lower
              hotel rates, and countless opportunities for winter sports.
            </p>
            <ul>
              <li>
                <strong>Pros:</strong> World-class skiing/snowboarding (e.g. Lake
                Louise, Sunshine Village), ice skating on frozen lakes, snowy
                panoramas with minimal crowds.
              </li>
              <li>
                <strong>Cons:</strong> Extreme cold, many hiking trails inaccessible
                or require snowshoes/ice cleats, short daylight hours.
              </li>
            </ul>
            <p>
              Always check road conditions and weather updates: snow tires
              or chains are required on certain routes, and closures can
              happen due to avalanche control. Layers, warm boots, and
              emergency car kits are essential in winter.
            </p>

            {/* SECTION 2: Spring */}
            <h2 id="section-2">Spring (April – May)</h2>
            <p>
              Spring is a transitional season with unpredictable weather—snow can
              linger at higher elevations until May. In the valleys, you’ll see
              early wildflowers and active wildlife, but some iconic high-elevation
              lakes remain frozen or inaccessible.
            </p>
            <ul>
              <li>
                <strong>Pros:</strong> Fewer tourists, possible late-season skiing,
                wildlife emerging from hibernation, moderate temps in lower
                elevations.
              </li>
              <li>
                <strong>Cons:</strong> Variable trail conditions (muddy/icy),
                limited access to certain roads, possible closures, and lakes may
                not have that famous turquoise color yet.
              </li>
            </ul>
            <p>
              Pack layers and traction aids for muddy or icy trails. Keep an
              eye out for bears just out of hibernation, and always carry bear
              spray.
            </p>

            {/* SECTION 3: Summer */}
            <h2 id="section-3">Summer (June – August)</h2>
            <p>
              Summer is peak season in the Rockies. Long days, accessible alpine
              trails, and iconic turquoise lakes make it a favorite time for
              many visitors. However, it’s also the busiest, with higher lodging
              prices and crowding at major attractions.
            </p>
            <ul>
              <li>
                <strong>Pros:</strong> Accessible high-elevation trails, best
                weather for outdoor activities, long daylight hours, fully
                thawed turquoise lakes.
              </li>
              <li>
                <strong>Cons:</strong> Large crowds, higher costs, potential
                wildfire smoke in late summer.
              </li>
            </ul>
            <p>
              Book accommodations and any guided experiences well in advance.
              Consider using shuttles (e.g., to Moraine Lake) to avoid parking
              hassles. Afternoon thunderstorms can occur—be prepared to adjust
              your schedule.
            </p>

            {/* SECTION 4: Fall */}
            <h2 id="section-4">Fall (September – November)</h2>
            <p>
              Fall is short but stunning, especially from mid to late September
              when larch trees turn golden. By October, early snowfall can
              blanket higher trails, and many tourist services wind down.
            </p>
            <ul>
              <li>
                <strong>Pros:</strong> Brilliant fall foliage (especially larch
                season), fewer crowds post-Labour Day, cool hiking weather.
              </li>
              <li>
                <strong>Cons:</strong> Some roads/trails begin closing in October,
                unpredictable weather, shorter daylight hours.
              </li>
            </ul>
            <p>
              If you time it right, fall can offer a quieter experience. Dress
              in layers and keep an eye on possible snow in higher passes.
            </p>

            {/* Section: Top Locations */}
            <h2 id="locations">Top Locations</h2>
            <p>
              The Canadian Rockies boast a wide range of must-see sites across
              multiple national parks. Below are some highlights, each with
              approximate coordinates so you can add them to your itinerary.
            </p>

            <ul>
              <li
                ref={(el) => setSpotRef("banff-national-park", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Banff National Park</strong>{" "}
                <Marker
                  coordY={51.17778}
                  coordX={-115.57361}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Canada’s first national park, famous for mountain vistas,
                abundant wildlife, and the vibrant town of Banff.
              </li>
              <li
                ref={(el) => setSpotRef("lake-louise", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Lake Louise</strong>{" "}
                <Marker
                  coordY={51.40699}
                  coordX={-116.22350}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Renowned for its turquoise water and stunning glacier backdrop;
                a starting point for teahouse hikes.
              </li>
              <li
                ref={(el) => setSpotRef("moraine-lake", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Moraine Lake</strong>{" "}
                <Marker
                  coordY={51.32250}
                  coordX={-116.18556}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Iconic “Valley of the Ten Peaks” view. Access via shuttle, as
                private vehicles aren’t allowed as of 2023.
              </li>
              <li
                ref={(el) => setSpotRef("icefields-parkway", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Icefields Parkway</strong>{" "}
                <Marker
                  coordY={51.42601}
                  coordX={-116.18553}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A breathtaking drive from Lake Louise to Jasper, dotted with
                viewpoints, glaciers, and turquoise lakes.
              </li>
              <li
                ref={(el) => setSpotRef("athabasca-glacier", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Athabasca Glacier</strong>{" "}
                <Marker
                  coordY={52.19861}
                  coordX={-117.24361}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Part of the Columbia Icefield. Visitors can walk to the glacier
                toe or take a guided tour on an Ice Explorer vehicle.
              </li>
              <li
                ref={(el) => setSpotRef("jasper-town", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Jasper National Park & Town</strong>{" "}
                <Marker
                  coordY={52.87306}
                  coordX={-118.08222}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – The largest national park in the Canadian Rockies, known for
                its laid-back vibe, wildlife sightings, and dark sky preserve
                for stargazing.
              </li>
              <li
                ref={(el) => setSpotRef("athabasca-falls", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Athabasca Falls</strong>{" "}
                <Marker
                  coordY={52.65750}
                  coordX={-117.88333}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A powerful waterfall near Jasper with easily accessible
                viewing platforms.
              </li>
              <li
                ref={(el) => setSpotRef("takakkaw-falls", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Takakkaw Falls</strong>{" "}
                <Marker
                  coordY={51.49806}
                  coordX={-116.47250}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – One of Canada’s tallest waterfalls, located in Yoho National
                Park. Spectacular flow in late spring and summer.
              </li>
              <li
                ref={(el) => setSpotRef("emerald-lake", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Emerald Lake</strong>{" "}
                <Marker
                  coordY={51.44400}
                  coordX={-116.53100}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – A BC gem in Yoho NP, known for its vivid color and surrounding
                forest trails.
              </li>
              <li
                ref={(el) => setSpotRef("radium-hot-springs", el)}
                style={{ scrollMarginTop: "80px" }}
              >
                <strong>Radium Hot Springs</strong>{" "}
                <Marker
                  coordY={50.61972}
                  coordX={-116.07306}
                  mobileMapRef={mobileMapRef}
                  mapRef={mapRef}
                />
                – Relax in mineral-rich hot pools located in Kootenay National
                Park’s scenic canyon area.
              </li>
            </ul>

            {/* SECTION: Local Regulations */}
            <h2 id="local-regulations">Local Regulations</h2>
            <p>
              National and provincial parks enforce specific rules to protect
              wildlife and the environment:
            </p>
            <ul>
              <li>
                <strong>Park Passes:</strong> A valid Park Pass is required for
                all Canadian national parks. Buy a day pass or annual Discovery
                Pass.
              </li>
              <li>
                <strong>Wildlife Safety:</strong> Maintain distances (at least
                30m from elk/deer, 100m from bears/wolves). Never feed wildlife.
                Bear spray is recommended on all hikes.
              </li>
              <li>
                <strong>Camping Permits:</strong> Front-country and backcountry
                camping require reservations and permits. No random camping
                outside designated areas.
              </li>
              <li>
                <strong>Leave No Trace:</strong> Stay on trails, pack out all
                garbage, and avoid picking wildflowers or removing natural
                objects.
              </li>
              <li>
                <strong>Vehicle Regulations:</strong> Snow tires or chains may
                be required in winter. Some roads close seasonally; heed closure
                signs and avalanche warnings.
              </li>
            </ul>
            <p className="disclaimer">
              <em>
                Disclaimer: Always check official park websites or contact local
                authorities for up-to-date rules, closures, and safety guidelines.
              </em>
            </p>

            {/* SECTION: FAQ */}
            <h2 id="faq">FAQ</h2>
            <div className="faq-section">
              <h3>1. What is the busiest season in the Canadian Rockies?</h3>
              <p>
                July and August are the most crowded, offering the best weather
                but also the highest prices. Book accommodations and tours well
                in advance if traveling in summer.
              </p>

              <h3>2. Can I visit without a car?</h3>
              <p>
                Yes—shuttle services, tour buses, and limited public transit are
                available. However, a car grants more flexibility. For popular
                spots like Moraine Lake, private cars are not permitted; you must
                use official park shuttles.
              </p>

              <h3>3. Is it safe to visit in winter?</h3>
              <p>
                Traveling in winter can be magical if you’re prepared. Be aware
                of road conditions, carry proper winter gear, and plan for sudden
                weather changes. Many resorts and trails cater specifically to
                winter activities.
              </p>
            </div>

            {/* SECTION: Conclusion */}
            <h2 id="conclusion">Conclusion</h2>
            <p>
              Whether you explore the vibrant wildflowers of spring, the
              turquoise lakes of summer, the golden larches of fall, or the
              snowy panoramas of winter, the Canadian Rockies are bound to
              leave you breathless. Each season offers something unique—from
              low-season tranquility to the peak-season buzz. By knowing the
              pros and cons of each time of year, following local guidelines,
              and preparing properly, you can make the most of your Rocky
              Mountain adventure. Now, pack your layers, charge your camera,
              and get ready for a trip of a lifetime!
            </p>

            {/* BOTTOM COMPONENT: references, author, related articles, etc. */}
            <Bottom referencesData={referencesData} ref_data={ref_data} />
          </article>

          {/* RIGHT SIDE COLUMN */}
          <Right currentArticle={ref_data} />
        </div>
      </main>

      {/* MOBILE MAP OVERLAY */}
      <MobileMapOverlay
        ref={mobileMapRef}
        markers={locations}
        spotRefs={spotRefs}
      />

      <Footer />
    </>
  );
}